import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader, Panel, LoadingState, ErrorState } from "@/components/common";
import { CandleChart } from "@/components/CandleChart";
import { Bitcoin } from "lucide-react";
import { cn } from "@/lib/utils";

const INTERVALS = ["15m", "1h", "4h", "1d", "1w"];
const fmt = (v, d = 2) => Number(v).toLocaleString("en-US", { maximumFractionDigits: d });

export function TickerTable({ items, selected, onSelect, testId = "crypto-ticker-table" }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid={testId}>
        <thead className="text-slate-500 uppercase text-[10px]">
          <tr><th className="text-left px-3 py-2">Pair</th><th className="text-right px-3">Last</th><th className="text-right px-3">24h %</th>
            <th className="text-right px-3 hidden md:table-cell">High</th><th className="text-right px-3 hidden md:table-cell">Low</th>
            <th className="text-right px-3 hidden lg:table-cell">Vol (USDT)</th><th className="text-right px-3">Range pos.</th></tr>
        </thead>
        <tbody>
          {items.map((t) => (
            <tr key={t.symbol} data-testid={`ticker-row-${t.symbol}`} onClick={() => onSelect?.(t.symbol)}
              className={cn("border-t border-[var(--border-subtle)] cursor-pointer hover:bg-[var(--card-hover)] transition-colors", selected === t.symbol && "bg-blue-500/10")}>
              <td className="px-3 py-2 font-semibold text-slate-200">{t.base}<span className="text-slate-500">/USDT</span></td>
              <td className="px-3 text-right font-mono-num text-slate-100">{fmt(t.last_price, 6)}</td>
              <td className={cn("px-3 text-right font-mono-num", Number(t.change_pct) >= 0 ? "text-emerald-400" : "text-rose-400")}>{fmt(t.change_pct)}%</td>
              <td className="px-3 text-right font-mono-num hidden md:table-cell text-slate-400">{fmt(t.high, 6)}</td>
              <td className="px-3 text-right font-mono-num hidden md:table-cell text-slate-400">{fmt(t.low, 6)}</td>
              <td className="px-3 text-right font-mono-num hidden lg:table-cell text-slate-400">{fmt(t.volume_quote, 0)}</td>
              <td className="px-3 text-right font-mono-num text-slate-300">{fmt(t.range_position_pct)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CryptoMarkets() {
  const [symbol, setSymbol] = useState("BTCUSDT");
  const [interval, setInterval] = useState("1h");
  const tick = useQuery({ queryKey: ["crypto-tickers"], refetchInterval: 15000,
    queryFn: async () => (await api.get("/market/crypto/tickers")).data });
  const kl = useQuery({ queryKey: ["crypto-klines", symbol, interval], refetchInterval: 30000,
    queryFn: async () => (await api.get("/market/crypto/klines", { params: { symbol, interval, limit: 300 } })).data });

  if (tick.isLoading) return <LoadingState label="Loading Binance market data..." />;
  if (tick.isError) return <ErrorState label="Binance public data unavailable" onRetry={tick.refetch} />;

  return (
    <div>
      <PageHeader title="Crypto Markets" subtitle="Binance public market data · read-only · no account access" icon={Bitcoin} demo={false} testId="crypto-header" />
      <Panel title={`${symbol} · ${interval}`} testId="crypto-chart-panel" className="mb-4"
        actions={<div className="flex gap-1">{INTERVALS.map((i) => (
          <button key={i} data-testid={`interval-${i}`} onClick={() => setInterval(i)}
            className={cn("px-2 py-1 rounded text-[11px] font-mono transition-colors", interval === i ? "bg-blue-500/20 text-blue-300" : "text-slate-400 hover:text-slate-100")}>{i}</button>))}</div>}>
        <div className="p-2">
          {kl.isLoading ? <LoadingState /> : kl.isError ? <ErrorState label="Chart data unavailable" onRetry={kl.refetch} />
            : <CandleChart candles={kl.data.candles} showZones={false} height={400} />}
        </div>
      </Panel>
      <Panel title="Pairs (live, refresh 15s)" testId="crypto-tickers-panel">
        <TickerTable items={tick.data.items} selected={symbol} onSelect={setSymbol} />
      </Panel>
    </div>
  );
}
